import type { NumberLocale } from "../core/parser/locale";
import { parseAmount, parseDate } from "../core/parser/locale";
import type {
  BalanceInfo,
  BankTemplate,
  ColumnLayout,
} from "../core/parser/template";
import type { TextLine } from "../core/types";

// A synthetic Serbian-style current-account statement ("Demo Banka").
// It exists so the whole pipeline — extraction, column parsing, multi-line memo
// gluing, reconciliation, export — can be built and tested end-to-end before a
// real bank PDF is available. Real templates follow exactly this shape.

export const DEMO_LOCALE: NumberLocale = {
  decimalSeparator: ",",
  thousandsSeparator: ".",
};

/**
 * Column x-ranges (PDF points), matched against each text item's horizontal
 * center. Shared with the fixture generator so layout stays in lock-step.
 */
export const DEMO_COLUMNS: ColumnLayout = {
  date: { xMin: 30, xMax: 104 },
  description: { xMin: 104, xMax: 325 },
  debit: { xMin: 325, xMax: 412 },
  credit: { xMin: 412, xMax: 500 },
  balance: { xMin: 500, xMax: 580 },
};

/** Drawing anchors used only by the fixture generator (kept next to columns). */
export const DEMO_DRAW = {
  pageWidth: 595,
  pageHeight: 842,
  fontSize: 9,
  headerFontSize: 10,
  dateX: 40,
  descX: 108,
  debitRightX: 405,
  creditRightX: 492,
  balanceRightX: 572,
  tableTopY: 660,
  rowHeight: 22,
};

export const demoBankTemplate: BankTemplate = {
  id: "rs-demo",
  name: "Demo Banka (пример)",
  country: "RS",
  currency: "RSD",
  numberLocale: DEMO_LOCALE,
  dateFormats: ["DD.MM.YYYY"],
  layout: DEMO_COLUMNS,
  dateCellPattern: /^\d{2}\.\d{2}\.\d{4}$/,
  stopRowPattern: /(kraj(nje)?|zavr[šs]no|po[cč]etno) stanje|ukupno|saldo/i,

  detect(fullText: string): number {
    const t = fullText.toLowerCase();
    // The synthetic template must only ever match its own fixture — never a real
    // statement — so gate everything behind its unique marker.
    if (!t.includes("demo banka")) return 0;
    let score = 0.6;
    if (t.includes("izvod")) score += 0.2;
    if (t.includes("promet po računu") || t.includes("promet po racunu"))
      score += 0.2;
    return Math.min(score, 1);
  },

  extractBalances(lines: TextLine[], locale: NumberLocale): BalanceInfo {
    const full = lines.map((l) => l.text).join("\n");

    const opening = matchAmount(
      full,
      /po[cč]etno stanje[^0-9\-]*([\-\d.,]+)/i,
      locale,
    );
    const closing = matchAmount(
      full,
      /(?:kraj(?:nje)?|zavr[šs]no) stanje[^0-9\-]*([\-\d.,]+)/i,
      locale,
    );

    const period =
      /period[:\s]*?(\d{2}\.\d{2}\.\d{4})\s*[-–—]\s*(\d{2}\.\d{2}\.\d{4})/i.exec(
        full,
      );
    const account = /ra[cč]un[:\s]*([0-9][0-9\-]+)/i.exec(full);

    return {
      opening,
      closing,
      periodStart: period ? parseDate(period[1]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      periodEnd: period ? parseDate(period[2]!, ["DD.MM.YYYY"]) ?? undefined : undefined,
      accountId: account ? account[1] : undefined,
    };
  },
};

function matchAmount(
  text: string,
  re: RegExp,
  locale: NumberLocale,
): number | null {
  const m = re.exec(text);
  if (!m || !m[1]) return null;
  return parseAmount(m[1], locale);
}
